import { Component, input, computed, output, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Stop } from '../../../../core/models/stop.model';
import { CardComponent } from '../../../../shared/ui/layout/card/card.component';
import { TextComponent } from '../../../../shared/ui/typography/text/text.component';
import { LucideIconComponent } from '../../../../shared/ui/lucide-icon/lucide-icon.component';
import { FormatTimePipe } from '../../../../shared/pipes/format-time.pipe';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { EnumLabelPipe } from '../../../../shared/pipes/enum-label.pipe';
import { convertAddressToGoogleUrl } from '../../../../shared/utilities/common-functions';
import {
  DocumentType,
  DocumentViewerService,
} from '../../../../core/services/document-viewer.service';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import {
  StopStatusOptionMap,
  StopStatusType,
} from '../../../../core/enums/stop-status.enum';
import { ColorVariant } from '../../../../core/enums/types';
import { toStopLocalString, formatDateTimeObject, guessTimezoneFromState } from '../../../../shared/utils/datetime.utils';

@Component({
  selector: 'app-load-stop-card',
  standalone: true,
  imports: [CommonModule, 
    CardComponent,
    TextComponent,
    ButtonComponent,
    LucideIconComponent,
    FormatTimePipe,
    EnumLabelPipe,
    BadgeComponent],
  templateUrl: './load-stop-card.component.html',
})
export class LoadStopCardComponent {
  stop = input.required<Stop>();
  canEdit = input<boolean>(true);
  canSplitHere = input<boolean>(false);
  servicedByLabel = input<string | null>(null);
  stickyNotesCount = input<number>(0);
  edit = output<void>();
  splitHere = output<void>();
  openStickyNotes = output<void>();
  convertAddressToGoogleUrl = convertAddressToGoogleUrl;

  private readonly documentViewer = inject(DocumentViewerService);

  isCompleted = computed(() => this.stop().status === 'DEPARTED');
  collapsed = signal(false);
  notesCollapsed = signal(true);

  protected readonly accentStyle = computed(() => {
    const type = this.stop()?.stopType;
    if (type === 'pickup') return 'info';
    if (type === 'delivery') return 'success';
    if (type === 'handoff') return 'warning';
    return 'secondary-dark';
  });

  protected readonly stopIconName = computed(() => {
    const type = this.stop()?.stopType;
    switch (type) {
      case 'pickup':
        return 'ArrowUp';
      case 'delivery':
        return 'ArrowDown';
      case 'both':
        return 'ArrowUpDown';
      case 'handoff':
        return 'ArrowRightLeft';
      case 'yard_drop':
      case 'yard_hook':
        return 'Warehouse';
      default:
        return 'MapPin';
    }
  });

  protected readonly stopTypeBadge = computed((): { label: string; variant: ColorVariant } | null => {
    const stop = this.stop();
    if (!stop) return null;
    if (stop.stopType === 'handoff' || stop.handoffToSegmentId) {
      return {
        label: stop.stopType === 'handoff' ? 'Relay Handoff' : 'Commercial + Relay',
        variant: 'warning',
      };
    }
    if (stop.stopType === 'yard_drop') return { label: 'Yard Drop', variant: 'secondary' };
    if (stop.stopType === 'yard_hook') return { label: 'Yard Hook', variant: 'secondary' };
    if (stop.stopType === 'both') return { label: 'Pick & Drop', variant: 'info' };
    if (stop.stopType === 'custom') return { label: 'Waypoint', variant: 'secondary' };
    return null;
  });

  protected readonly hasRelayTracking = computed(() => {
    const stop = this.stop();
    return !!(stop?.handoffToSegmentId || stop?.relay);
  });

  protected readonly outgoingDropDone = computed(() => {
    const stop = this.stop();
    return stop?.relay?.dropStatus === 'COMPLETED' || stop?.status === 'DEPARTED';
  });

  protected readonly oncomingHookDone = computed(() => {
    const stop = this.stop();
    return stop?.relay?.hookStatus === 'COMPLETED' || stop?.status === 'DEPARTED';
  });

  protected readonly outgoingDroppedDisplay = computed(() => {
    const ts = this.stop()?.relay?.dropCompletedAt;
    return ts ? toStopLocalString(ts, this.stopTz(), 'short') : null;
  });

  protected readonly oncomingHookedDisplay = computed(() => {
    const ts = this.stop()?.relay?.hookCompletedAt;
    return ts ? toStopLocalString(ts, this.stopTz(), 'short') : null;
  });

  statusInfo = computed(() => {
    const status = (this.stop()?.status || 'PENDING') as StopStatusType;
    return (
      StopStatusOptionMap[status] || {
        label: status,
        color: 'secondary' as ColorVariant,
        icon: 'Clock',
      }
    );
  });

  private stopTz = computed(() => this.stop()?.timezone || (this.stop()?.address?.state ? guessTimezoneFromState(this.stop()?.address?.state) : undefined));

  arrivedDisplay = computed(() => {
    const ts = this.stop()?.arrivedAt;
    const dto = this.stop()?.arrivalDateTime;
    if (ts) return toStopLocalString(ts, this.stopTz(), 'short');
    if (dto?.date && dto?.time) return formatDateTimeObject(dto);
    return null;
  });

  checkInDisplay = computed(() => {
    const ts = this.stop()?.checkedInAt;
    const dto = this.stop()?.checkInDateTime;
    if (ts) return toStopLocalString(ts, this.stopTz(), 'short');
    if (dto?.date && dto?.time) return formatDateTimeObject(dto);
    return null;
  });

  dockedDisplay = computed(() => {
    const ts = this.stop()?.dockedAt;
    if (ts) return toStopLocalString(ts, this.stopTz(), 'short');
    return null;
  });

  checkOutDisplay = computed(() => {
    const ts = this.stop()?.checkedOutAt;
    const dto = this.stop()?.checkOutDateTime;
    if (ts) return toStopLocalString(ts, this.stopTz(), 'short');
    if (dto?.date && dto?.time) return formatDateTimeObject(dto);
    return null;
  });

  departedDisplay = computed(() => {
    const ts = this.stop()?.departedAt;
    const dto = this.stop()?.departureDateTime;
    if (ts) return toStopLocalString(ts, this.stopTz(), 'short');
    if (dto?.date && dto?.time) return formatDateTimeObject(dto);
    return null;
  });

  etaDisplay = computed(() => {
    const dto = this.stop()?.etaDateTime;
    if (dto?.date && dto?.time) return formatDateTimeObject(dto);
    return null;
  });

  constructor() {
    effect(() => {
      if (this.isCompleted()) {
        this.collapsed.set(true);
      }
    });
  }

  protected stopLabel = computed(() => `Stop ${this.stop()?.stopNumber}`);

  protected getStopDocUrl(docField: any): string | null {
    if (!docField) return null;
    if (typeof docField === 'string') {
      return docField === 'N/A' ? null : docField.trim() || null;
    }
    if (Array.isArray(docField)) {
      const latest = docField[docField.length - 1];
      return this.getStopDocUrl(latest);
    }
    if (typeof docField === 'object' && typeof docField.url === 'string') {
      return docField.url === 'N/A' ? null : docField.url.trim() || null;
    }
    return null;
  }

  protected hasStopDocuments = computed(() => {
    const stop = this.stop();
    const docs = stop?.documents;
    if (!docs) return false;

    return !!(
      this.getStopDocUrl(docs.billOfLading) ||
      this.getStopDocUrl(docs.proofOfDelivery) ||
      this.getStopDocUrl(docs.loadImage) ||
      this.getStopDocUrl(docs.sealImage) ||
      this.getStopDocUrl(docs.temperatureImage) ||
      this.getStopDocUrl(docs.lumperReceipt)
    );
  });

  viewDocument = output<{ url: string; title: string; documentCategory: string }>();

  viewStopDocument(
    documentKey: string,
    url: string,
    title: string,
    _type: DocumentType = 'image'
  ) {
    const stop = this.stop();
    const stopNumber = stop?.stopNumber;
    if (!url || !stop || stopNumber == null) return;

    this.viewDocument.emit({
      url,
      title: `${title} - Stop ${stopNumber}`,
      documentCategory: `stop-${stopNumber}-${documentKey}`
    });
  }
}
